import { useForm } from 'react-hook-form'

import Image from 'next/image'

import { ApolloError } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'

import { User, UserDataDocument, useSignInMutation, useUserDataQuery } from '~/app/generated/graphql'
import { useMarketContext } from '~/components/SettingsProvider/MarketClientContext'
import { gt } from '~/lib/gt'

import { FormInput } from '../../FormInput/FormInput'
import { useToast } from '../../Toast'
import sharedStyles from '../AuthModals.module.css'
import { ModalTitle, SocialButtons, SubmitButton } from '../AuthModalsShards'

import { formatErrorMessages } from './formatErrorMessages'
import styles from './LoginModal.module.css'
import { LOGIN_FORM_FIELDS, loginSchema } from './LoginModalUtils'

const mergedStyles = { ...styles, ...sharedStyles }

export type SignInCallback = (user: Pick<User, 'id'> | null) => void

type LoginFormProps = {
  onLogin?: SignInCallback
  onRegisterLinkClick?: () => void
  handleClose: () => void
  onForgotPasswordClick?: () => void
}

export const LoginModal = ({ onLogin, onRegisterLinkClick, handleClose, onForgotPasswordClick }: LoginFormProps) => {
  return (
    <div className={mergedStyles.modal}>
      <div className={mergedStyles.formWrapper}>
        <LoginForm
          handleClose={handleClose}
          onLogin={onLogin}
          onRegisterLinkClick={onRegisterLinkClick}
          onForgotPasswordClick={onForgotPasswordClick}
        />
      </div>
      <div className={mergedStyles.imageWrapper}>
        <Image src='/lp/assets/login/loginFormPhoto.png' objectFit={'cover'} height={750} width={500} loading='eager' alt='Login Form' />
      </div>
    </div>
  )
}

export const LoginForm = ({ onLogin, onRegisterLinkClick, handleClose, onForgotPasswordClick }: LoginFormProps) => {
  const { refetch } = useUserDataQuery()
  const { supportEmail } = useMarketContext()

  const [signIn] = useSignInMutation({
    refetchQueries: [{ query: UserDataDocument }, 'UserData'],
    onCompleted: data => onLogin?.(data.signIn?.user ?? null),
  })
  const toast = useToast()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, touchedFields },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(loginSchema),
  })

  const handleErrors = (error: ApolloError) => {
    const errors = formatErrorMessages(error, { supportEmail })
    errors?.forEach(errorMsg =>
      toast?.add({
        message: errorMsg,
        type: 'error',
        containerSelector: '#LoginToastContainer',
        size: 'small',
      })
    )
  }

  const handleSocialLogin: SignInCallback = data => {
    if (!data) return
    onLogin?.(data)
    refetch()
    handleClose()
  }

  const onSubmit = async (values: { email: string; password: string }) => {
    await signIn({
      variables: values,
      onCompleted: handleClose,
      onError: handleErrors,
    })
  }

  return (
    <form className={mergedStyles.form} onSubmit={handleSubmit(onSubmit)} data-color-scheme='white'>
      <ModalTitle title={gt.tp('LoginForm', 'Log in')} />

      <h5 className={mergedStyles.noAccount}>
        {gt.tp('LoginForm', 'Not registered yet?')}
        <button type='button' className={mergedStyles.noAccountButton} onClick={onRegisterLinkClick}>
          {gt.tp('LoginForm', 'Create an account')}
        </button>
      </h5>

      <div id='LoginToastContainer' className={mergedStyles.toastContainer} />

      {LOGIN_FORM_FIELDS.map(field => (
        <div key={field.name}>
          <FormInput
            {...register(field.name)}
            type={field.type}
            label={gt.tp('LoginForm', field.title)}
            className={mergedStyles.input}
            errorMessage={touchedFields[field.name] ? errors[field.name]?.message : ''}
          />
        </div>
      ))}
      <div className={mergedStyles.formLinkContainer} onClick={onForgotPasswordClick}>
        {gt.tp('LoginForm', 'Forgot password?')}
        <div className={mergedStyles.line} />
      </div>

      <SubmitButton disabled={!isValid || isSubmitting} label={gt.tp('LoginForm', 'Log in')} />
      <div className={mergedStyles.otherLogin}>{gt.tp('Or', 'or')}</div>

      <SocialButtons onLogin={handleSocialLogin} />
    </form>
  )
}
